<template>
  <div class="location-select w-full min-w-0">
    <QuickAddSelectField
      v-model="model"
      :input-id="inputId"
      :label="label"
      :float-labels="floatLabels"
      :options="shapedOptions"
      :option-label="optionLabel"
      :option-value="optionValue"
      :required="required"
      :disabled="disabled"
      :invalid="invalid"
      :invalid-message="invalidMessage"
      :filter="filter"
      :show-clear="showClear"
      :loading="loading"
      :can-add="canAdd"
      :can-edit="canEdit"
      :can-delete="canDelete"
      :add-disabled="addDisabled"
      :edit-disabled="editDisabled"
      :delete-disabled="editDisabled"
      :add-disabled-message="addDisabledMessage"
      :edit-disabled-message="editDisabledMessage"
      delete-disabled-message="Select a location first"
      delete-confirm-header="Delete location"
      :delete-confirm-message="deleteConfirmMessage"
      :add-label="addLabel"
      :create-placeholder="createPlaceholder"
      :context-hint="contextHint"
      :hint="hint"
      :max-length="maxLength"
      :create-option="createOption"
      :delete-option="handleDeleteOption"
      :display-variant="displayVariant"
      :filter-fields="filterFields"
      @edit="openEdit"
    />

    <LocationEditModal
      ref="modalRef"
      v-model:visible="modalVisible"
      :saving="saving"
      :show-status="false"
      :can-delete="canDelete"
      :base-z-index="1300"
      :locations="allLocations"
      @save="handleSave"
      @delete="handleModalDelete"
    />
  </div>
</template>

<script setup>
import { computed, nextTick, ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import QuickAddSelectField from '@/components/forms/QuickAddSelectField.vue';
import LocationEditModal from '@/features/settings/components/modals/LocationEditModal.vue';
import { orgReferenceApi, invalidateOrgReferenceOptions } from '@/api/orgReferenceApi';
import {
  normalizeReferenceId,
  removeOption,
  shapeLocationOption,
  upsertOption,
} from '@/features/org-references/utils/referenceOptionUtils';

const props = defineProps({
  label: { type: String, default: 'Primary work site' },
  floatLabels: { type: Boolean, default: true },
  options: { type: Array, default: () => [] },
  optionLabel: { type: String, default: 'name' },
  optionValue: { type: String, default: 'id' },
  required: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  invalid: { type: Boolean, default: false },
  invalidMessage: { type: String, default: '' },
  filter: { type: Boolean, default: true },
  showClear: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  canAdd: { type: Boolean, default: false },
  canEdit: { type: Boolean, default: false },
  canDelete: { type: Boolean, default: false },
  addDisabled: { type: Boolean, default: false },
  editDisabled: { type: Boolean, default: false },
  addDisabledMessage: { type: String, default: '' },
  editDisabledMessage: { type: String, default: '' },
  addLabel: { type: String, default: 'Create New Location' },
  createPlaceholder: { type: String, default: 'New location name' },
  contextHint: { type: String, default: '' },
  hint: { type: String, default: '' },
  maxLength: { type: Number, default: 160 },
  inputId: { type: String, default: '' },
  createOption: { type: Function, default: null },
  applySavedOption: { type: Function, default: null },
  allLocations: { type: Array, default: () => [] },
  displayVariant: { type: String, default: 'rich' },
  filterFields: { type: Array, default: () => [] },
});

const emit = defineEmits(['saved', 'deleted']);
const model = defineModel({ default: null });

const shapedOptions = computed(() => (props.options || []).map((row) => shapeLocationOption(row)));
const toast = useToast();
const confirm = useConfirm();

const modalRef = ref(null);
const modalVisible = ref(false);
const saving = ref(false);
const canDelete = computed(() => props.canDelete || props.canEdit);

const deleteConfirmMessage = computed(() => {
  const row = findOption(model.value);
  const name = row?.name || 'this location';
  return `Delete "${name}"? Employee and team location links will be cleared. This cannot be undone.`;
});

function findOption(valueOrRow) {
  if (valueOrRow && typeof valueOrRow === 'object') {
    const fromRow = normalizeReferenceId(valueOrRow.id ?? valueOrRow.value);
    if (fromRow != null) {
      const match = (props.options || []).find(
        (row) =>
          normalizeReferenceId(row?.[props.optionValue] ?? row?.id ?? row?.value) === fromRow
      );
      return { ...(match || {}), ...valueOrRow };
    }
    return valueOrRow;
  }
  const id = normalizeReferenceId(valueOrRow);
  if (id == null) return null;
  return (
    (props.options || []).find(
      (row) => normalizeReferenceId(row?.[props.optionValue] ?? row?.id ?? row?.value) === id
    ) || { id, name: '' }
  );
}

function openEdit(option) {
  const row = findOption(option ?? model.value);
  if (!row) return;
  modalRef.value?.openEdit?.(shapeLocationOption(row));
  modalVisible.value = true;
}

async function handleSave({ mode, payload }) {
  if (mode !== 'edit') return;

  saving.value = true;
  try {
    const saved = await orgReferenceApi.locations.update(payload);
    const shaped = shapeLocationOption(saved);
    if (typeof props.applySavedOption === 'function') {
      await props.applySavedOption(shaped);
    } else if (Array.isArray(props.options)) {
      upsertOption(props.options, shaped);
      invalidateOrgReferenceOptions();
    } else {
      invalidateOrgReferenceOptions();
    }
    const nextId = shaped[props.optionValue] ?? shaped.id ?? shaped.value;
    if (nextId != null) model.value = nextId;
    modalVisible.value = false;
    emit('saved', shaped);
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err?.message || 'Failed to update location.',
      life: 3500,
    });
  } finally {
    saving.value = false;
  }
}

function dropLocationFromLists(id) {
  if (Array.isArray(props.allLocations)) {
    removeOption(props.allLocations, id);
  }
  if (Array.isArray(props.options)) {
    removeOption(props.options, id);
  }
  invalidateOrgReferenceOptions();
}

async function handleDeleteOption(option) {
  const row = findOption(option ?? model.value);
  const id = normalizeReferenceId(row?.id ?? row?.value);
  if (id == null) {
    throw new Error('Select a location first.');
  }

  saving.value = true;
  try {
    await orgReferenceApi.locations.delete({ location_id: id });
    dropLocationFromLists(id);
    if (normalizeReferenceId(model.value) === id) {
      model.value = null;
    }
    modalVisible.value = false;
    emit('deleted', { id, name: row?.name || '' });
    toast.add({
      severity: 'success',
      summary: 'Location deleted',
      detail: `"${row?.name || 'Location'}" was removed from the catalog.`,
      life: 2500,
    });
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err?.message || 'Failed to delete location.',
      life: 3500,
    });
  } finally {
    saving.value = false;
  }
}

function handleModalDelete(payload) {
  modalVisible.value = false;
  nextTick(() => {
    const row = findOption(payload);
    const name = row?.name || payload?.name || 'this location';
    confirm.require({
      header: 'Delete location',
      message: `Delete "${name}"? Employee and team location links will be cleared. This cannot be undone.`,
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: 'Cancel',
      rejectProps: { severity: 'secondary', outlined: true },
      acceptLabel: 'Delete',
      acceptProps: { severity: 'danger' },
      accept: () => {
        handleDeleteOption(row || payload).catch(() => {});
      },
    });
  });
}
</script>
